import { useState, useRef, useEffect, useMemo } from 'react';
import { authFetch } from '../../lib/rpc';
import { reportDataFetchError } from '../../lib/telemetry';
import { CdrRecord } from './cdrTypes';

interface UseCdrDataOptions {
  serverId: string;
  fetchCdrs: (offset: number, limit: number, search: string, startDate?: string, endDate?: string, filters?: string[], skipCount?: boolean, searchArchive?: boolean) => Promise<{ data: any[]; total?: number | null; extensions?: Record<string, string>; isArchive?: boolean }>;
  audioDownloadUrlPrefix: string;
  resolveCnamMap?: (numbers: string[]) => Promise<Record<string, string>>;
}

export function useCdrData(
  optionsOrServerId: UseCdrDataOptions | string,
  maybeFetchCdrs?: (offset: number, limit: number, search: string, startDate?: string, endDate?: string, filters?: string[], skipCount?: boolean, searchArchive?: boolean) => Promise<{ data: any[]; total?: number | null; extensions?: Record<string, string>; isArchive?: boolean }>,
  maybeResolveCnamMap?: (numbers: string[]) => Promise<Record<string, string>>,
  maybeAudioDownloadUrlPrefix?: string
) {
  const options: UseCdrDataOptions = typeof optionsOrServerId === 'string'
    ? {
        serverId: optionsOrServerId,
        fetchCdrs: maybeFetchCdrs!,
        resolveCnamMap: maybeResolveCnamMap,
        audioDownloadUrlPrefix: maybeAudioDownloadUrlPrefix || ''
      }
    : optionsOrServerId;
  const { serverId, fetchCdrs, audioDownloadUrlPrefix, resolveCnamMap } = options;

  const [cdrs, setCdrs] = useState<CdrRecord[]>([]);
  const [cdrsTotal, setCdrsTotal] = useState(0);
  const [cdrsPage, setCdrsPage] = useState(0);
  const [cdrsSearch, setCdrsSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchArchive, setSearchArchive] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>(['hide-feature-codes']);
  const [cdrsLoading, setCdrsLoading] = useState(false);
  const [cdrsLastRefreshed, setCdrsLastRefreshed] = useState<Date | null>(null);
  const [isWsConnected, setIsWsConnected] = useState(false);
  const [pendingNewCallsCount, setPendingNewCallsCount] = useState(0);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [globalExtNames, setGlobalExtNames] = useState<Record<string, string>>({});
  const [cnamMap, setCnamMap] = useState<Record<string, string>>({});
  const [expandedCdrId, setExpandedCdrId] = useState<string | null>(null);
  const [expandedMissedSegments, setExpandedMissedSegments] = useState<Record<string, boolean>>({});

  const cdrsRef = useRef(cdrs);
  cdrsRef.current = cdrs;
  const pageRef = useRef(cdrsPage);
  pageRef.current = cdrsPage;
  const searchRef = useRef(cdrsSearch);
  searchRef.current = cdrsSearch;
  const startDateRef = useRef(startDate);
  startDateRef.current = startDate;
  const endDateRef = useRef(endDate);
  endDateRef.current = endDate;
  const searchArchiveRef = useRef(searchArchive);
  searchArchiveRef.current = searchArchive;
  const activeFiltersRef = useRef(activeFilters);
  activeFiltersRef.current = activeFilters;
  const currentFetchIdRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);

  const archiveCacheRef = useRef<{
    queryKey: string;
    allGroups: any[][];
    total: number;
    extensions?: any;
  } | null>(null);

  useEffect(() => {
    if (highlightedId) {
      const tmr = setTimeout(() => setHighlightedId(null), 2500);
      return () => clearTimeout(tmr);
    }
  }, [highlightedId]);

  const toggleCdrDetails = (cdr: any) => {
    setExpandedCdrId(prev => prev === cdr.id ? null : cdr.id);
  };

  const formatSeconds = (sec: number | string) => {
    const n = typeof sec === 'string' ? parseInt(sec, 10) || 0 : sec;
    const m = Math.floor(n / 60);
    const s = n % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const buildGroupsFromRecords = (records: any[]) => {
    const grouped: any[][] = [];
    const linkedIds = new Set<string>();
    const primaryGroupMap = new Map<string, any[]>();

    records.forEach((cdr: any) => {
      const groupId = cdr.call_group_id || cdr.linkedid || cdr.uniqueid;
      if (groupId && !linkedIds.has(groupId)) {
        const related = records.filter((c: any) => (c.call_group_id || c.linkedid || c.uniqueid) === groupId);
        related.forEach(() => linkedIds.add(groupId));
        primaryGroupMap.set(groupId, related.sort((a: any, b: any) => new Date(a.calldate).getTime() - new Date(b.calldate).getTime()));
      } else if (!groupId) {
        grouped.push([cdr]);
      }
    });

    primaryGroupMap.forEach((recList) => {
      grouped.push(recList);
    });

    return grouped.sort((a, b) => {
      const timeA = new Date(a[a.length - 1].calldate).getTime();
      const timeB = new Date(b[b.length - 1].calldate).getTime();
      return timeB - timeA;
    });
  };

  const loadCdrs = async (page = 0, search = cdrsSearch, sDate = startDate, eDate = endDate, filters = activeFilters, silent = false, isArchive = searchArchiveRef.current) => {
    const fetchId = ++currentFetchIdRef.current;
    const queryKey = `${serverId}|${search.trim()}|${sDate}|${eDate}|${JSON.stringify(filters)}`;

    if (isArchive && archiveCacheRef.current && archiveCacheRef.current.queryKey === queryKey) {
      const cache = archiveCacheRef.current;
      const startIdx = page * 25;
      const endIdx = startIdx + 25;
      if (startIdx < cache.allGroups.length) {
        const pageGroups = cache.allGroups.slice(startIdx, endIdx);
        const pageRecords = pageGroups.flat();
        setCdrs(pageRecords);
        setCdrsTotal(cache.total);
        setCdrsPage(page);
        if (!silent) setExpandedCdrId(null);
        setCdrsLoading(false);
        setCdrsLastRefreshed(new Date());
        return;
      }
    }

    if (!silent) setCdrsLoading(true);
    try {
      const isPaging = (page > 0 && cdrsTotal > 0) || silent;
      const res = await fetchCdrs(page * 25, 25, search, sDate, eDate, filters, isPaging, isArchive);
      if (fetchId !== currentFetchIdRef.current) return;

      const { data: rawData = [], total, extensions } = res || {};
      if (res && res.isArchive !== undefined) {
        setSearchArchive(!!res.isArchive);
      }

      const seenIds = new Set();
      const data = rawData.filter((c: any) => {
        const uid = c.uniqueid ? `${c.uniqueid}_${c.sequence || ''}` : c.id;
        if (seenIds.has(uid)) return false;
        seenIds.add(uid);
        return true;
      });

      if (extensions) {
        setGlobalExtNames(extensions);
      }

      if (isArchive) {
        const allGroups = buildGroupsFromRecords(data);
        archiveCacheRef.current = {
          queryKey,
          allGroups,
          total: total || allGroups.length,
          extensions
        };
        const startIdx = page * 25;
        const endIdx = startIdx + 25;
        const pageGroups = allGroups.slice(startIdx, endIdx);
        setCdrs(pageGroups.length > 0 ? pageGroups.flat() : data.slice(0, 50));
      } else {
        setCdrs(data);
      }

      if (total !== undefined && total !== null) {
        setCdrsTotal(total);
      }
      setCdrsPage(page);
      if (!silent) setExpandedCdrId(null);
      if (page === 0 && !search.trim() && !sDate && !eDate) {
        setPendingNewCallsCount(0);
      }

      if (resolveCnamMap) {
        const missingNumbers = new Set<string>();
        const isExternal = (num: string) => {
          if (!num) return false;
          if (num.toLowerCase() === 'anonymous' || num.toLowerCase() === 'unknown') return true;
          const digits = num.replace(/\D/g, '');
          return digits.length >= 7 || num.startsWith('+');
        };

        data.forEach((cdr: any) => {
          if (isExternal(cdr.src) && (!cdr.cnam || cdr.cnam === 'unknown' || cdr.cnam === cdr.src)) missingNumbers.add(cdr.src);
          if (isExternal(cdr.dst) && (!cdr.dst_cnam || cdr.dst_cnam === 'unknown' || cdr.dst_cnam === cdr.dst)) missingNumbers.add(cdr.dst);
        });

        const needed = Array.from(missingNumbers).filter(num => !cnamMap[num]);
        if (needed.length > 0) {
          resolveCnamMap(needed).then(freshMap => {
            if (Object.keys(freshMap).length > 0) {
              setCnamMap(prev => ({ ...prev, ...freshMap }));
            }
          }).catch(err => {
            console.error("Failed to resolve dynamic CNAMs in batch:", err);
          });
        }
      }
    } catch (err: any) {
      console.error('[CDR Fetch Error]', err);
      reportDataFetchError('crm', 'CDR', err, { serverId, page, search });
      if (fetchId === currentFetchIdRef.current) {
        setCdrs([]);
        setCdrsTotal(0);
      }
    }
    if (fetchId === currentFetchIdRef.current) {
      setCdrsLoading(false);
      setCdrsLastRefreshed(new Date());
    }
  };

  useEffect(() => {
    if (serverId) {
      if (searchArchive) return;
      const tmr = setTimeout(() => {
        loadCdrs(0, cdrsSearch, startDate, endDate, activeFilters);
      }, 300);
      return () => clearTimeout(tmr);
    }
  }, [startDate, endDate, serverId, activeFilters, cdrsSearch, searchArchive]);

  useEffect(() => {
    if (searchArchive) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    let isMounted = true;
    let retryCount = 0;

    const connectWebSocket = () => {
      if (!isMounted) return;
      try {
        if (!audioDownloadUrlPrefix) return;
        const match = audioDownloadUrlPrefix.match(/\/api\/v1\/crm-dashboard\/([^/]+)/);
        if (!match) return;
        const slug = match[1];

        const parsedUrl = new URL(audioDownloadUrlPrefix, window.location.origin);
        const crmPw = parsedUrl.searchParams.get('x-crm-password') || '';

        const wsHost = parsedUrl.host || window.location.host;
        const wsProtocol = parsedUrl.protocol === 'https:' ? 'wss:' : (parsedUrl.protocol === 'http:' ? 'ws:' : (window.location.protocol === 'https:' ? 'wss:' : 'ws:'));
        const wsUrl = `${wsProtocol}//${wsHost}/api/v1/crm-dashboard/${slug}/ws${crmPw ? `?pw=${encodeURIComponent(crmPw)}` : ''}`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          retryCount = 0;
          setIsWsConnected(true);
          ws.send(JSON.stringify({ type: 'AUTH', password: crmPw }));
          if (cdrsRef.current.length > 0 && pageRef.current === 0 && !searchRef.current.trim() && !startDateRef.current && !endDateRef.current) {
            loadCdrs(0, '', '', '', activeFiltersRef.current, true);
          }
        };

        ws.onmessage = (event) => {
          try {
            if (searchArchiveRef.current) return;
            const data = JSON.parse(event.data);
            if (data.type === 'CDR_NEW' || data.type === 'NEW_CDR') {
              const incoming = Array.isArray(data.records) ? data.records : (data.record ? [data.record] : (data.cdr ? [data.cdr] : []));
              if (incoming.length === 0) return;

              if (pageRef.current === 0 && !searchRef.current.trim() && !startDateRef.current && !endDateRef.current) {
                setCdrs(prev => {
                  let next = [...prev];
                  for (const newCdr of incoming) {
                    const matchIdx = next.findIndex(c =>
                      (newCdr.uniqueid && c.uniqueid === newCdr.uniqueid && (newCdr.sequence ? c.sequence === newCdr.sequence : (!c.sequence || c.channel === newCdr.channel))) ||
                      (newCdr.id && c.id === newCdr.id)
                    );
                    if (matchIdx >= 0) {
                      next[matchIdx] = { ...next[matchIdx], ...newCdr };
                    } else {
                      next = [newCdr, ...next];
                    }
                  }
                  return next.slice(0, 50);
                });
                setCdrsTotal(prev => prev + incoming.length);
                const first = incoming[0];
                if (first) setHighlightedId(first.uniqueid || first.id);
              } else {
                setPendingNewCallsCount(prev => prev + incoming.length);
              }
            } else if (data.type === 'PBX_CDR_UPDATED' || data.type === 'REFRESH_CDRS') {
              loadCdrs(pageRef.current, searchRef.current, startDateRef.current, endDateRef.current, activeFiltersRef.current, true);
            }
          } catch {}
        };

        ws.onerror = () => {
          setIsWsConnected(false);
        };

        ws.onclose = () => {
          setIsWsConnected(false);
          if (isMounted && !searchArchive) {
            const delay = Math.min(1000 * Math.pow(2, retryCount), 10000);
            retryCount++;
            setTimeout(connectWebSocket, delay);
          }
        };
      } catch {}
    };

    connectWebSocket();

    return () => {
      isMounted = false;
      setIsWsConnected(false);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [searchArchive, audioDownloadUrlPrefix]);

  const cdrsGroups = useMemo(() => {
    const grouped: CdrRecord[][] = [];
    const linkedIds = new Set<string>();
    const primaryGroupMap = new Map<string, CdrRecord[]>();

    cdrs.forEach((cdr) => {
      const groupId = cdr.call_group_id || cdr.linkedid || cdr.uniqueid;
      if (groupId && !linkedIds.has(groupId)) {
        const related = cdrs.filter((c) => (c.call_group_id || c.linkedid || c.uniqueid) === groupId);
        related.forEach(() => linkedIds.add(groupId));
        primaryGroupMap.set(groupId, related.sort((a, b) => new Date(a.calldate).getTime() - new Date(b.calldate).getTime()));
        grouped.push(primaryGroupMap.get(groupId)!);
      } else if (!groupId) {
        grouped.push([cdr]);
      }
    });
    return grouped;
  }, [cdrs]);

  return {
    cdrs,
    setCdrs,
    cdrsTotal,
    setCdrsTotal,
    cdrsPage,
    setCdrsPage,
    cdrsSearch,
    setCdrsSearch,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchArchive,
    setSearchArchive,
    activeFilters,
    setActiveFilters,
    cdrsLoading,
    cdrsLastRefreshed,
    isWsConnected,
    pendingNewCallsCount,
    setPendingNewCallsCount,
    highlightedId,
    globalExtNames,
    cnamMap,
    cdrsGroups,
    expandedCdrId,
    setExpandedCdrId,
    expandedMissedSegments,
    setExpandedMissedSegments,
    toggleCdrDetails,
    formatSeconds,
    loadCdrs
  };
}
